import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	Output,
	ViewChild,
	viewChild,
} from '@angular/core';
import OptionGroup from '../../../smoothr-web-app-core/models/OptionGroup';
import ArticleOption from '../../../smoothr-web-app-core/models/ArticleOption';
import { DisplayIdentifier } from '../../../smoothr-web-app-core/enums/DisplayIdentifier';
import Article from '../../../smoothr-web-app-core/models/Article';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { OrderType } from '../../../smoothr-web-app-core/enums/OrderType';
import { getAvailability } from '../../../smoothr-web-app-core/utils/utils';
import { AppComponent } from 'src/app/app.component';

@Component({
	selector: 'app-article-option-group-custom',
	templateUrl: './article-option-group-custom.component.html',
	styleUrls: ['article-option-group-custom.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArticleOptionGroupCustomComponent implements OnInit, AfterViewInit {
	optionGroupValue: OptionGroup;
	@ViewChild('wrapperArticles') wrapperArticles: ElementRef<HTMLElement>;
	@Input() set optionGroup(value: OptionGroup) {
		if (value) {
			value.articles = value.articles.filter(it => !this.isHidden(it) && !it.hidden);
			this.optionGroupValue = value;
		}
	}
	@Input() article: Article;
	@Output() selection = new EventEmitter<ArticleOption>();
	@Input() preorderType: PreorderType;
	@Input() currency: string;
	@Input() customView: boolean = false;
	@Input() showAllButton = true;
	di = DisplayIdentifier;
	showFirstArticles = false;
	showAmount = AppComponent.largeScreen ? 4 : 3;
	showBackIcon = false;
	showNextIcon = false;
	private focusableItems: HTMLElement[] = [];
	private currentFocusIndex: number = -1;
	constructor() {}

	private _selected: ArticleOption[] = [];

	get selected(): ArticleOption[] {
		return this._selected;
	}

	@Input()
	set selected(value: ArticleOption[]) {
		this._selected = value;
	}

	ngOnInit() {
		this.showAllButton = this.optionGroupValue?.articles?.length > 4;
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.updateScrollIcons();
			this.setupKeyboardNavigation();
		}, 1000);
	}
	private setupKeyboardNavigation(): void {
		// Add keyboard listeners to the wrapper
		const wrapper = this.wrapperArticles?.nativeElement;
		if (wrapper) {
			wrapper.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
		}
	}
	private handleKeyboardNavigation(event: KeyboardEvent): void {
		// Skip if no focusable items
		if (!this.focusableItems.length) return;

		// Find the currently focused element
		const focusedElement = document.activeElement as HTMLElement;
		this.currentFocusIndex = this.focusableItems.indexOf(focusedElement);

		switch (event.key) {
			case 'ArrowRight':
				// Move focus to the next item
				if (this.currentFocusIndex < this.focusableItems.length - 1) {
					this.currentFocusIndex++;
					this.focusableItems[this.currentFocusIndex].focus();
					event.preventDefault();
				}
				break;

			case 'ArrowLeft':
				// Move focus to the previous item
				if (this.currentFocusIndex > 0) {
					this.currentFocusIndex--;
					this.focusableItems[this.currentFocusIndex].focus();
					event.preventDefault();
				}
				break;

			case 'Home':
				// Move focus to the first item
				this.currentFocusIndex = 0;
				this.focusableItems[this.currentFocusIndex].focus();
				event.preventDefault();
				break;

			case 'End':
				// Move focus to the last item
				this.currentFocusIndex = this.focusableItems.length - 1;
				this.focusableItems[this.currentFocusIndex].focus();
				event.preventDefault();
				break;
		}
	}
	ionViewDidEnter() {
		console.log('DID ENTER');
		this.updateScrollIcons();
	}
	onAdd(article: Article) {
		const articleOption = new ArticleOption();
		articleOption.article = article;
		articleOption.group = this.optionGroupValue._id;
		articleOption.quantity = 1;
		this.selection.emit(articleOption);
	}

	onRemove(article: Article) {
		const option = new ArticleOption();
		option.article = article;
		option.quantity = -1;
		option.group = this.optionGroupValue._id;
		this.selection.emit(option);
	}

	count(article: Article) {
		return this.selected.filter(ao => ao.article._id === article._id).reduce((prev, curr) => prev + curr.quantity, 0);
	}

	isHidden(article: Article): boolean {
		return this.preorderType && !getAvailability(article, OrderType.PREORDER, this.preorderType);
	}
	findArticleBrot(optionGroup: OptionGroup) {
		return optionGroup.articles.find(it => it.visible);
	}
	showAll(value: boolean) {
		this.showFirstArticles = value;
	}
	checkIfModifierSelectExist(optionGroup: OptionGroup) {
		return !!(optionGroup?.tags ?? []).find(it => it?.identifier === 'modifier_select');
	}
	checkIfModifierRemoveExist(optionGroup: OptionGroup) {
		return !!(optionGroup?.tags ?? []).find(it => it?.identifier === 'modifier_remove');
	}
	scrollBack() {
		const wrapper = this.wrapperArticles.nativeElement;
		wrapper.scrollBy({
			top: 0,
			left: -wrapper.clientWidth,
			behavior: 'smooth',
		});
	}
	scrollForward() {
		const wrapper = this.wrapperArticles.nativeElement;
		wrapper.scrollBy({
			top: 0,
			left: wrapper.clientWidth,
			behavior: 'smooth',
		});
	}

	onScroll() {
		this.updateScrollIcons();
	}

	updateScrollIcons() {
		if (this.showAmount && this.optionGroupValue?.articles && this.optionGroupValue?.articles?.length <= 3) {
			this.showBackIcon = false;
			this.showNextIcon = false;
			return;
		}
		const wrapper = this.wrapperArticles.nativeElement;
		const maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;
		this.showBackIcon = wrapper.scrollLeft > 0;
		this.showNextIcon = wrapper.scrollLeft < maxScrollLeft;
	}
	onShowAllClicked(): void {
		this.showFirstArticles = !this.showFirstArticles;

		// Update focusable items after layout changes
		setTimeout(() => {
			this.checkScrollPosition();
		}, 300); // After transition completes
	}
	private checkScrollPosition(): void {
		if (this.wrapperArticles && this.wrapperArticles.nativeElement) {
			const container = this.wrapperArticles.nativeElement;

			// Show back arrow if not at the start
			this.showBackIcon = container.scrollLeft > 10;

			// Show forward arrow if not at the end
			const isAtEnd = container.scrollLeft + container.offsetWidth >= container.scrollWidth - 10;
			this.showNextIcon = !isAtEnd && !this.showFirstArticles;

			// Update ARIA attributes for screen readers
			container.setAttribute('aria-describedby', 'scrollStatus');
		}
	}
	private updateScrollAnnouncement(message: string): void {
		const statusElement = document.getElementById('scrollStatus');
		if (statusElement) {
			statusElement.textContent = message;

			// Clear the announcement after it's been read
			setTimeout(() => {
				statusElement.textContent = '';
			}, 3000);
		}
	}

	/**
	 * Listen for global resize events to update UI
	 */
	@HostListener('window:resize')
	onResize(): void {
		// Check if scroll arrows need to be shown/hidden
		this.checkScrollPosition();

		// Update focusable items in case the layout changed
	}
}
